import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { WholesaleBillingSettingsType } from '../../types/wholesale-billing-settings/wholesaleBillingSettings.types';
import {
  createWholesaleBillingSettings,
  getWholesaleBillingSettings,
  updateWholesaleBillingSettings,
} from './wholesaleBillingSettings.action';

export interface WholesaleBillingSettingsState {
  wholesaleBillingSettings: WholesaleBillingSettingsType | null;
  wholesaleBillingSettingsFetchStatus: FETCH_STATUS;
  wholesaleBillingSettingsCreateStatus: FETCH_STATUS;
  wholesaleBillingSettingsUpdateStatus: FETCH_STATUS;
}

const initialState: WholesaleBillingSettingsState = {
  wholesaleBillingSettings: null,
  wholesaleBillingSettingsFetchStatus: FETCH_STATUS.NULL,
  wholesaleBillingSettingsCreateStatus: FETCH_STATUS.NULL,
  wholesaleBillingSettingsUpdateStatus: FETCH_STATUS.NULL,
};

const wholesaleBillingSettingsSlice = createSlice({
  name: 'wholesaleBillingSettings',
  initialState,
  reducers: {
    setWholesaleBillingSettings: (state, action: PayloadAction<WholesaleBillingSettingsType | null>) => {
      state.wholesaleBillingSettings = action.payload;
    },
    setWholesaleBillingSettingsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.wholesaleBillingSettingsFetchStatus = action.payload;
    },
    setWholesaleBillingSettingsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.wholesaleBillingSettingsCreateStatus = action.payload;
    },
    setWholesaleBillingSettingsUpdateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.wholesaleBillingSettingsUpdateStatus = action.payload;
    },
    resetWholesaleBillingSettingsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getWholesaleBillingSettings.pending, state => {
        state.wholesaleBillingSettingsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getWholesaleBillingSettings.fulfilled, (state, action) => {
        state.wholesaleBillingSettingsFetchStatus = FETCH_STATUS.FULFILLED;
        state.wholesaleBillingSettings = action.payload;
      })
      .addCase(getWholesaleBillingSettings.rejected, state => {
        state.wholesaleBillingSettingsFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(createWholesaleBillingSettings.pending, state => {
        state.wholesaleBillingSettingsCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createWholesaleBillingSettings.fulfilled, (state, action) => {
        state.wholesaleBillingSettingsCreateStatus = FETCH_STATUS.FULFILLED;
        state.wholesaleBillingSettings = action.payload;
      })
      .addCase(createWholesaleBillingSettings.rejected, state => {
        state.wholesaleBillingSettingsCreateStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(updateWholesaleBillingSettings.pending, state => {
        state.wholesaleBillingSettingsUpdateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateWholesaleBillingSettings.fulfilled, (state, action) => {
        state.wholesaleBillingSettingsUpdateStatus = FETCH_STATUS.FULFILLED;
        state.wholesaleBillingSettings = {
          ...action.payload,
          revioData: state.wholesaleBillingSettings?.revioData,
        };
      })
      .addCase(updateWholesaleBillingSettings.rejected, state => {
        state.wholesaleBillingSettingsUpdateStatus = FETCH_STATUS.REJECTED;
      });
  },
});

export const {
  setWholesaleBillingSettings,
  setWholesaleBillingSettingsFetchStatus,
  setWholesaleBillingSettingsCreateStatus,
  setWholesaleBillingSettingsUpdateStatus,
  resetWholesaleBillingSettingsState,
} = wholesaleBillingSettingsSlice.actions;

export default wholesaleBillingSettingsSlice.reducer;
